import {
  boolean,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uuid,
} from "drizzle-orm/pg-core";
import { tenant } from "../auth";

export const pricingSourceEnum = pgEnum("pricing_source", [
  "TCGPLAYER",
  "MANUAL",
  "STOCKX",
  "FANATICS",
  /** The value follows a synced Shopify variant's price (`shopify_variants`). */
  "SHOPIFY",
  "OTHER",
]);

/**
 * Matches boxed `AvailabilityStrategy`: DYNAMIC (source as needed) vs FIXED
 * (require on-hand before box use; depleted qty can't be used in a box).
 */
export const availabilityStrategyEnum = pgEnum("availability_strategy", ["DYNAMIC", "FIXED"]);

export const fulfillableStatusEnum = pgEnum("fulfillable_status", [
  "NO_RISK",
  "MEDIUM_RISK",
  "HIGH_RISK",
  "UNFULFILLABLE",
  "SUPER_UNFULFILLABLE",
  "ARCHIVED",
]);

/**
 * Where a catalog item is in its life, separate from the risk grade in `fulfillable_status`.
 * Almost every item stays STANDARD. PRE_RELEASE holds an item for a box that has not launched:
 * usable in that box, nowhere else, until the box goes live. ARCHIVED retires it.
 */
export enum CatalogItemStatus {
  STANDARD = "STANDARD",
  PRE_RELEASE = "PRE_RELEASE",
  ARCHIVED = "ARCHIVED",
}

export const catalogItemStatusEnum = pgEnum("catalog_item_status", [
  CatalogItemStatus.STANDARD,
  CatalogItemStatus.PRE_RELEASE,
  CatalogItemStatus.ARCHIVED,
]);

/**
 * Whether an item may be used outside official boxes (forge, exchange, vending, daily box,
 * lootgames, packs). Stock and risk checks still apply on top of OPEN.
 */
export enum Circulation {
  OPEN = "OPEN",
  OFFICIAL_ONLY = "OFFICIAL_ONLY",
}

export const circulationEnum = pgEnum("circulation", [Circulation.OPEN, Circulation.OFFICIAL_ONLY]);

/** Fails to compile if a member is missing from a pgEnum list above. */
type Assert<T extends true> = T;
type _CatalogItemStatusCoverage = Assert<
  CatalogItemStatus extends (typeof catalogItemStatusEnum.enumValues)[number] ? true : false
>;
type _CirculationCoverage = Assert<
  Circulation extends (typeof circulationEnum.enumValues)[number] ? true : false
>;

export const catalogItems = pgTable(
  "catalog_items",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    name: text("name").notNull(),
    image: text("image"),
    overrideImage: text("override_image"),
    sourceId: text("source_id"),
    value: integer("value"),
    marketPrice: integer("market_price"),
    /**
     * Platform-admin-authored data. Current shape:
     * { compareAtValue?: number; compareAtLabelOverride?: string }.
     * Keep vendor/protocol data in `attributes`.
     */
    platformData: jsonb("platform_data"),
    pricingSource: pricingSourceEnum("pricing_source").notNull().default("MANUAL"),
    pricingRef: jsonb("pricing_ref"),
    availabilityStrategy: availabilityStrategyEnum("availability_strategy")
      .notNull()
      .default("DYNAMIC"),
    // Facility stock + fulfillability signals (quantity/withdrawing tracked here directly).
    quantity: integer("quantity").notNull().default(0),
    withdrawingCount: integer("withdrawing_count").notNull().default(0),
    listingCount: integer("listing_count"),
    userInventoryCount: integer("user_inventory_count"),
    supplyQuantity: integer("supply_quantity"),
    fulfillableStatus: fulfillableStatusEnum("fulfillable_status")
      .notNull()
      .default("UNFULFILLABLE"),
    overrideFulfillableStatus: fulfillableStatusEnum("override_fulfillable_status"),
    /** Lifecycle; see {@link CatalogItemStatus}. */
    status: catalogItemStatusEnum("status").notNull().default(CatalogItemStatus.STANDARD),
    /**
     * The policy answer (protocol, category, blacklist, limited) as the consumer that owns those
     * rules computed it; Boxed syncs it. Read through `effectiveCirculation`, never directly.
     */
    circulation: circulationEnum("circulation").notNull().default(Circulation.OPEN),
    /** Admin override of `circulation`. PRE_RELEASE and ARCHIVED still win over it. */
    overrideCirculation: circulationEnum("override_circulation"),
    /** Archive the item once its last unit ships (slabs, one-of-ones). */
    autoArchive: boolean("auto_archive").notNull().default(false),
    category: text("category"),
    subcategory: text("subcategory"),
    type: text("type"),
    tags: text("tags").array().notNull().default([]),
    attributes: jsonb("attributes"),
    isInForge: boolean("is_in_forge").notNull().default(false),
    isInMarketplace: boolean("is_in_marketplace").notNull().default(false),
    /** No longer written or read; `GET /catalog/in-box` answers from `drop_table_items`. Dropped by the contract migration. */
    isInBox: boolean("is_in_box").notNull().default(false),
    /** Owning tenant. NULL means the item is shared across every tenant. */
    tenantId: text("tenant_id").references(() => tenant.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // Browse / admin list by taxonomy (replaces boxed protocolIndex).
    index("catalog_items_category_idx").on(t.category),
    index("catalog_items_fulfillable_status_idx").on(t.fulfillableStatus),
    index("catalog_items_status_idx").on(t.status),
    index("catalog_items_marketplace_idx").on(t.isInMarketplace),
    index("catalog_items_in_box_idx").on(t.isInBox),
    index("catalog_items_source_id_idx").on(t.sourceId),
    index("catalog_items_tenant_idx").on(t.tenantId),
  ],
);
